import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import PropTypes from 'prop-types';
import {
  gateTitleToHeading,
  GATE_HELP_TEXT,
  GATE_SET_OPTIONS,
  parseGateMovedVariables,
  parseInvalidGateTypes,
  getHoverPosition,
  invalidDragsToRemove
} from '../helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GateTooltip from './gate_tooltip';

import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';

const GATE_PROPS = PropTypes.arrayOf(PropTypes.shape({
  id: PropTypes.number.isRequired,
  order: PropTypes.number.isRequired,
  gateType: PropTypes.string.isRequired,
  requiredBefore: PropTypes.string.isRequired
}));

export default function MoveableGate({
  allowedDropLocations,
  dragIdentifier,
  gates,
  index,
  previousGateType,
  title,
  type,
  zIndex,
  onGatesUpdated,
  onInvalidDrag,
  onRemoveInvalidDrags,
}) {
  const ref = useRef(null);
  const canDrag = !GATE_SET_OPTIONS.includes(title);
  const isInvalid = GATE_SET_OPTIONS.includes(title) && GATE_SET_OPTIONS.includes(previousGateType);
  const [{ handlerId }, drop] = useDrop({
    accept: dragIdentifier,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) return;

      const { index: dragIndex, title: dragTitle, type: dragType } = item;
      const hoverIndex = index;
      const hoverType = type;

      // early return if we are hovering over same item
      if (dragIndex === hoverIndex && dragType === hoverType) return;

      const placementLocation = getHoverPosition(ref, monitor);

      // forbidden gate position
      if (!allowedDropLocations[placementLocation]) return;

      // early return since this will lead the gate back to the same position
      if (dragIndex < hoverIndex && placementLocation === 'left' && dragType === hoverType) return;
      if (dragIndex > hoverIndex && placementLocation === 'right' && dragType === hoverType) return;

      const {
        type: newType,
        index: newIndex,
        handleGatesMoveFunction
      } = parseGateMovedVariables(dragType, hoverType, placementLocation, hoverIndex, dragIndex);
      const newGates = handleGatesMoveFunction(gates, hoverType, dragType, hoverIndex, dragIndex);
      const invalidGatesTypes = parseInvalidGateTypes(newGates);
      if (invalidGatesTypes.length > 0) {
        onInvalidDrag(invalidGatesTypes);
        return;
      }

      onRemoveInvalidDrags(invalidDragsToRemove(dragTitle));
      if (newType) item.type = newType;
      if (newIndex) item.index = newIndex;
      onGatesUpdated(newGates);
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: dragIdentifier,
    item: () => {
      return { dragIdentifier, index, type, title };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    isDragging: monitor => monitor.getItem()?.title === title && monitor.getItem()?.dragIdentifier === dragIdentifier,
    end: () => {
      onRemoveInvalidDrags(invalidDragsToRemove(title), 6000);
    },
    canDrag: canDrag
  });

  drag(drop(ref));

  const titleToClass = title.toLowerCase().replace('_', '-');
  const cssClasses = `panel-default moveable-gate flex-grow-1 d-flex position-relative gap-2
  justify-content-center align-items-center ${titleToClass} ${canDrag ? 'cursor-move' : ''}
  ${isDragging ? 'is-dragging' : ''} ${isInvalid ? 'invalid-gate' : ''}`;

  const gateHeading = gateTitleToHeading(title);

  const invalidTooltipText = (
    <div>
      <div>{gateHeading} is <span className="fw-semibold text-decoration-underline">inaccessible.</span></div>
      Please add an action prior to {gateHeading} to make it accessible
    </div>
  );

  const toolTipText = isInvalid ? invalidTooltipText : GATE_HELP_TEXT[title];

  return (
    <div className={cssClasses} data-handler-id={handlerId} ref={ref} style={{ zIndex: isDragging ? 1000 : zIndex }}>
      {isDragging ? null : (
        <React.Fragment>
          {canDrag && (<FontAwesomeIcon className="mr-2" icon={faGripDotsVertical} size="lg"/>)}
          {gateHeading}
          <GateTooltip text={toolTipText} title={title}/>
        </React.Fragment>
      )}
    </div>
  );
}

MoveableGate.propTypes = {
  allowedDropLocations: PropTypes.shape({
    left: PropTypes.bool.isRequired,
    right: PropTypes.bool.isRequired
  }).isRequired,
  dragIdentifier: PropTypes.string.isRequired,
  gates: PropTypes.shape({
    broker: GATE_PROPS,
    owner: GATE_PROPS
  }),
  index: PropTypes.number.isRequired,
  previousGateType: PropTypes.string,
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  zIndex: PropTypes.number.isRequired,
  onGatesUpdated: PropTypes.func.isRequired,
  onInvalidDrag: PropTypes.func.isRequired,
  onRemoveInvalidDrags: PropTypes.func.isRequired,
};
